import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Check,
  CheckCircle2,
  Clock,
  Cloud,
  Loader2,
  Paperclip,
  ShieldCheck,
  Video,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { submitShortApplication, createMeetingBooking } from "@/lib/api";
import SmsConsent from "@/components/SmsConsent";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const MAX_MB = 15;
const TIME_SLOTS = [
  "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
  "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM",
];

const POLICY_TYPES = ["Term life", "Whole life", "Universal life", "Final expense", "Not sure"];
const LOOKING_FOR = [
  "Term life cover",
  "Whole life cover",
  "Final expense cover",
  "Just comparing prices",
  "Not sure yet",
  "Something else",
];

/** Question schema — each step renders a single question. */
const QUESTIONS = [
  {
    id: "intro",
    type: "intro",
    label: "Let's get to know each other.",
    hint: "Just the basics — takes 30 seconds.",
    fields: [
      { id: "name", type: "text", placeholder: "Full name" },
      { id: "phone", type: "tel", placeholder: "Phone number" },
      { id: "email", type: "email", placeholder: "Email address" },
      { id: "address", type: "text", placeholder: "Home address" },
      { id: "dob", type: "date", placeholder: "Date of birth" },
    ],
  },
  {
    id: "profile",
    type: "profile",
    label: "A few quick questions.",
    hint: "Tap Yes or No — it takes ten seconds.",
    rows: [
      { id: "has_advisor", prompt: "Do you currently have a financial advisor?" },
      { id: "has_insurance", prompt: "Do you have life insurance today?" },
      { id: "has_policy", prompt: "Do you have a copy of your current policy handy?" },
    ],
  },
  {
    id: "insured_details",
    type: "insured",
    label: "Your current cover.",
    hint: "A rough figure is fine — your advisor will confirm the details.",
  },
  {
    id: "looking_for",
    type: "looking",
    label: "What are you looking for?",
    hint: "Pick the closest fit — we'll take it from there.",
  },
  { id: "policy_file", type: "file", label: "Attach your policy", hint: "PDF, image, or Word doc — up to 15 MB. Skip if you don't have it handy." },
];

/** Which questions apply for a given set of answers. */
function visibleFor(a) {
  return QUESTIONS.filter((q) => {
    if (q.id === "policy_file") return a.has_policy === true;
    if (q.id === "insured_details") return a.has_insurance === true;
    if (q.id === "looking_for") return a.has_insurance === false;
    return true;
  });
}

export default function ShortApplication() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    dob: "",
    has_advisor: null,
    has_insurance: null,
    has_policy: null,
    premium_amount: "",
    insurance_company: "",
    policy_type: "",
    looking_for: "",
    looking_for_other: "",
    policy_file: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { name, email, phone }

  const visibleQuestions = useMemo(() => visibleFor(answers), [answers]);
  const current = visibleQuestions[Math.min(step, visibleQuestions.length - 1)];
  const total = visibleQuestions.length;
  const pct = Math.round(((step + 1) / total) * 100);
  const isLast = step === total - 1;

  const set = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const validate = () => {
    const q = current;
    if (q.type === "intro") {
      if (!String(answers.name || "").trim()) return "Please share your name.";
      if (String(answers.phone || "").replace(/\D/g, "").length < 10) return "Please enter a valid phone number.";
      if (!EMAIL_RE.test(String(answers.email || ""))) return "Please enter a valid email address.";
      if (!String(answers.address || "").trim()) return "Please add your home address.";
      if (!String(answers.dob || "").trim()) return "Please add your date of birth.";
      return null;
    }
    if (q.type === "profile") {
      const missing = q.rows.find((r) => answers[r.id] !== true && answers[r.id] !== false);
      if (missing) return "Please answer every question — Yes or No.";
      return null;
    }
    if (q.type === "insured") {
      if (!String(answers.premium_amount || "").trim()) return "Roughly how much are you paying each month?";
      if (!String(answers.insurance_company || "").trim()) return "Which company is your policy with?";
      if (!String(answers.policy_type || "").trim()) return "What type of policy is it? Pick the closest fit.";
      return null;
    }
    if (q.type === "looking") {
      if (!String(answers.looking_for || "").trim()) return "Pick the closest fit — or 'Not sure yet'.";
      if (answers.looking_for === "Something else" && !String(answers.looking_for_other || "").trim())
        return "Tell us what you're looking for.";
      return null;
    }
    // file question is optional
    return null;
  };

  const next = () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    if (isLast) return submit();
    setStep((s) => s + 1);
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submitDirect = async (data) => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", data.name.trim());
      fd.append("phone", data.phone.trim());
      fd.append("email", data.email.trim());
      fd.append("address", data.address.trim());
      fd.append("dob", data.dob.trim());
      fd.append("has_advisor", String(!!data.has_advisor));
      fd.append("has_insurance", String(!!data.has_insurance));
      fd.append("has_policy", String(!!data.has_policy));
      if (data.premium_amount) fd.append("premium_amount", data.premium_amount.trim());
      if (data.insurance_company) fd.append("insurance_company", data.insurance_company.trim());
      if (data.policy_type) fd.append("policy_type", data.policy_type);
      if (data.looking_for) {
        const lf = data.looking_for === "Something else" ? (data.looking_for_other || "").trim() : data.looking_for;
        if (lf) fd.append("looking_for", lf);
      }
      if (data.policy_file) fd.append("policy_file", data.policy_file);
      await submitShortApplication(fd);
      setDone({ name: data.name.trim(), email: data.email.trim(), phone: data.phone.trim() });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit — please try again.");
    } finally {
      setSubmitting(false);
    }
  };
  const submit = () => submitDirect(answers);

  // Auto-advance (or auto-submit) once all profile rows are answered.
  const pickProfile = (rowId, val) => {
    const newAnswers = { ...answers, [rowId]: val };
    setAnswers(newAnswers);
    if (current.type !== "profile") return;
    const allDone = current.rows.every((r) => {
      const v = r.id === rowId ? val : newAnswers[r.id];
      return v === true || v === false;
    });
    if (!allDone) return;
    const newVisible = visibleFor(newAnswers);
    const nextIdx = step + 1;
    setTimeout(() => {
      if (nextIdx >= newVisible.length) {
        submitDirect(newAnswers);
      } else {
        setStep(nextIdx);
      }
    }, 350);
  };

  if (done) {
    return <BookingCard done={done} navigate={navigate} />;
  }

  return (
    <div className="min-h-screen bg-sand flex flex-col" data-testid="short-application">
      {/* Header + progress */}
      <header className="sticky top-0 z-30 bg-cream/90 backdrop-blur-md border-b border-forest/10">
        <div className="mx-auto max-w-3xl px-6 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate("/")} className="flex items-center gap-2" data-testid="sa-logo">
              <span className="grid place-items-center w-8 h-8 rounded-full bg-forest text-cream">
                <Cloud className="h-4 w-4" />
              </span>
              <span className="font-serif text-xl text-forest">Advanced Life Policies</span>
            </button>
            <span className="text-xs font-semibold text-forest/60" data-testid="sa-step-label">
              {step + 1} / {total}
            </span>
          </div>
          <div className="mt-3">
            <Progress value={pct} className="h-1.5" data-testid="sa-progress" />
          </div>
        </div>
      </header>

      {/* Question */}
      <main className="flex-1 flex items-center">
        <div className="mx-auto max-w-2xl w-full px-6 py-16">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              initial={{ opacity: 0, x: 32 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -32 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`sa-q-${current.id}`}
            >
              <QuestionRow index={step} q={current} answers={answers} set={set} pickProfile={pickProfile} onEnter={next} />
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {/* Nav */}
      <footer className="sticky bottom-0 bg-cream/90 backdrop-blur-md border-t border-forest/10">
        <div className="mx-auto max-w-3xl px-6 py-4 flex items-center justify-between gap-4">
          <Button
            onClick={back}
            disabled={step === 0}
            variant="outline"
            data-testid="sa-back"
            className="rounded-full border-forest/25 bg-transparent text-forest h-12 px-6 disabled:opacity-40"
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <Button
            onClick={next}
            disabled={submitting}
            data-testid="sa-next"
            className={`rounded-full text-cream h-12 px-8 font-semibold ${isLast ? "bg-terracotta hover:bg-terracotta-dark" : "bg-forest hover:bg-forest-700"}`}
          >
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : isLast ? (
              <>Submit <Check className="h-4 w-4 ml-2" /></>
            ) : (
              <>Continue <ArrowRight className="h-4 w-4 ml-2" /></>
            )}
          </Button>
        </div>
        <p className="pb-3 text-center text-xs text-forest/45 flex items-center justify-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5" /> Your info is private and encrypted.
        </p>
      </footer>
    </div>
  );
}

const INPUT_CLS = "h-14 rounded-2xl bg-white border-forest/20 text-forest text-lg placeholder:text-forest/35 px-5";

function QuestionRow({ index, q, answers, set, pickProfile, onEnter }) {
  const inputRef = useRef(null);
  React.useEffect(() => {
    if (inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 80);
    }
  }, [q.id]);

  const num = String(index + 1).padStart(2, "0");
  const value = answers[q.id];

  return (
    <div>
      <p className="text-xs font-bold tracking-[0.2em] uppercase text-sage">Question {num}</p>
      <h1 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-forest">
        {q.label}
      </h1>
      {q.hint && <p className="mt-3 text-forest/60">{q.hint}</p>}

      <div className="mt-8">
        {q.type === "intro" && (
          <div className="space-y-3 max-w-lg">
            {q.fields.map((f, i) => (
              <div key={f.id}>
                {f.id === "dob" && (
                  <span className="text-[11px] uppercase tracking-[0.14em] font-bold text-sage mb-1.5 block">Date of birth</span>
                )}
                <Input
                  ref={i === 0 ? inputRef : undefined}
                  type={f.type}
                  value={answers[f.id] || ""}
                  placeholder={f.placeholder}
                  max={f.type === "date" ? format(new Date(), "yyyy-MM-dd") : undefined}
                  onChange={(e) => set(f.id, e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && onEnter()}
                  data-testid={`sa-${f.id}`}
                  className={INPUT_CLS}
                />
              </div>
            ))}
            <SmsConsent className="pt-2" />
          </div>
        )}

        {q.type === "profile" && (
          <div className="space-y-5 max-w-2xl">
            {q.rows.map((row) => {
              const v = answers[row.id];
              return (
                <div key={row.id} className="rounded-2xl bg-white border border-forest/10 p-5 sm:flex sm:items-center sm:justify-between gap-6">
                  <p className="text-forest text-base sm:text-lg font-semibold sm:font-medium leading-snug">
                    {row.prompt}
                  </p>
                  <div className="mt-3 sm:mt-0 grid grid-cols-2 gap-2 sm:w-56 shrink-0">
                    {[
                      { label: "Yes", val: true },
                      { label: "No", val: false },
                    ].map((opt) => {
                      const active = v === opt.val;
                      return (
                        <button
                          key={opt.label}
                          type="button"
                          onClick={() => pickProfile(row.id, opt.val)}
                          data-testid={`sa-${row.id}-${opt.label.toLowerCase()}`}
                          className={`h-11 rounded-xl border-2 text-sm font-semibold transition-all duration-150 ${
                            active
                              ? "border-forest bg-forest text-cream"
                              : "border-forest/15 bg-white text-forest hover:border-forest/40"
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {q.type === "insured" && (
          <div className="space-y-3 max-w-lg">
            <div>
              <span className="text-[11px] uppercase tracking-[0.14em] font-bold text-sage mb-1.5 block">How much are you paying per month?</span>
              <Input
                ref={inputRef}
                type="text"
                inputMode="decimal"
                value={answers.premium_amount || ""}
                placeholder="e.g. $85"
                onChange={(e) => set("premium_amount", e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onEnter()}
                data-testid="sa-premium-amount"
                className={INPUT_CLS}
              />
            </div>
            <div>
              <span className="text-[11px] uppercase tracking-[0.14em] font-bold text-sage mb-1.5 block">Which company?</span>
              <Input
                type="text"
                value={answers.insurance_company || ""}
                placeholder="e.g. Northwestern Mutual"
                onChange={(e) => set("insurance_company", e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onEnter()}
                data-testid="sa-insurance-company"
                className={INPUT_CLS}
              />
            </div>
            <div>
              <span className="text-[11px] uppercase tracking-[0.14em] font-bold text-sage mb-1.5 block">What type of policy?</span>
              <Select value={answers.policy_type} onValueChange={(v) => set("policy_type", v)}>
                <SelectTrigger className="h-14 rounded-2xl bg-white border-forest/20 text-forest text-lg px-5" data-testid="sa-policy-type">
                  <SelectValue placeholder="Pick the closest fit" />
                </SelectTrigger>
                <SelectContent data-testid="sa-policy-type-options">
                  {POLICY_TYPES.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`sa-policy-type-${t.toLowerCase().replace(/\s+/g, "-")}`}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {q.type === "looking" && (
          <div className="space-y-3 max-w-lg">
            <Select value={answers.looking_for} onValueChange={(v) => set("looking_for", v)}>
              <SelectTrigger className="h-14 rounded-2xl bg-white border-forest/20 text-forest text-lg px-5" data-testid="sa-looking-for">
                <SelectValue placeholder="Pick the closest fit" />
              </SelectTrigger>
              <SelectContent data-testid="sa-looking-for-options">
                {LOOKING_FOR.map((t) => (
                  <SelectItem key={t} value={t} data-testid={`sa-looking-for-${t.toLowerCase().replace(/\s+/g, "-")}`}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {answers.looking_for === "Something else" && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                <Input
                  type="text"
                  value={answers.looking_for_other || ""}
                  placeholder="Tell us what you're looking for"
                  onChange={(e) => set("looking_for_other", e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && onEnter()}
                  data-testid="sa-looking-for-other"
                  className={INPUT_CLS}
                  autoFocus
                />
              </motion.div>
            )}
          </div>
        )}

        {q.type === "file" && (
          <FileDrop
            file={value}
            onFile={(f) => set(q.id, f)}
            testid={`sa-${q.id}`}
          />
        )}
      </div>
    </div>
  );
}

function FileDrop({ file, onFile, testid }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const handleFiles = (files) => {
    const f = files?.[0];
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`File too large. Please keep it under ${MAX_MB} MB.`);
      return;
    }
    onFile(f);
  };

  if (file) {
    return (
      <div className="rounded-2xl border border-forest/15 bg-white p-5 flex items-center gap-4" data-testid={`${testid}-selected`}>
        <span className="grid place-items-center h-12 w-12 rounded-xl bg-sage/15 text-forest">
          <Paperclip className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-forest truncate">{file.name}</p>
          <p className="text-xs text-forest/55">{(file.size / 1024).toFixed(1)} KB · {file.type || "unknown"}</p>
        </div>
        <button
          type="button"
          onClick={() => onFile(null)}
          data-testid={`${testid}-remove`}
          className="text-forest/50 hover:text-terracotta transition-colors"
          aria-label="Remove file"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        handleFiles(e.dataTransfer.files);
      }}
      data-testid={testid}
      className={`w-full rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${
        drag ? "border-forest bg-white" : "border-forest/25 bg-white/60 hover:border-forest/40"
      }`}
    >
      <Paperclip className="h-7 w-7 mx-auto text-forest/60" />
      <p className="mt-3 font-semibold text-forest">Click or drop your policy here</p>
      <p className="mt-1 text-xs text-forest/55">PDF, PNG, JPG, or DOCX · up to {MAX_MB} MB</p>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept=".pdf,image/*,.doc,.docx"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`${testid}-input`}
      />
    </button>
  );
}

function BookingCard({ done, navigate }) {
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [notes, setNotes] = useState("");
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(null);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const book = async () => {
    if (!date) return toast.error("Please pick a date.");
    if (!time) return toast.error("Please pick a time.");
    setBooking(true);
    try {
      const res = await createMeetingBooking({
        name: done.name,
        email: done.email,
        phone: done.phone,
        meeting_date: format(date, "yyyy-MM-dd"),
        meeting_time: time,
        notes: notes.trim(),
      });
      setBooked(res);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't book — please try again.");
    } finally {
      setBooking(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-sand px-6 py-12" data-testid="short-app-booking">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-lg w-full rounded-3xl bg-white border border-forest/10 shadow-xl p-8 sm:p-10 text-center"
      >
        <span className="grid place-items-center w-16 h-16 rounded-full bg-sage/20 mx-auto">
          <CheckCircle2 className="h-8 w-8 text-forest" />
        </span>

        {booked ? (
          <>
            <h1 className="mt-6 font-serif text-3xl text-forest">You&apos;re booked in!</h1>
            <p className="mt-3 text-forest/70">
              Thanks, {done.name.split(" ")[0]}. We&apos;ll send a Google Calendar invite (with Meet link) to <strong>{done.email}</strong> for <strong>{format(new Date(booked.meeting_date + "T00:00:00"), "EEE, MMM d")}</strong> at <strong>{booked.meeting_time}</strong>.
            </p>
            {booked.meet_link && (
              <a
                href={booked.meet_link}
                target="_blank"
                rel="noreferrer"
                data-testid="booked-meet-link"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-forest hover:bg-forest-700 text-cream px-5 py-3 font-semibold transition-colors"
              >
                <Video className="h-4 w-4" /> Open Google Meet link
              </a>
            )}
            <button
              onClick={() => navigate("/")}
              data-testid="booked-home-btn"
              className="mt-6 block mx-auto text-sm text-forest/50 hover:text-forest underline underline-offset-4 transition-colors"
            >
              Back to home
            </button>
          </>
        ) : (
          <>
            <h1 className="mt-6 font-serif text-3xl text-forest">Request received!</h1>
            <p className="mt-3 text-forest/70">
              Thanks, {done.name.split(" ")[0]}. Pick a time below and we&apos;ll send a Google Meet invite straight to your inbox.
            </p>

            <div className="mt-7 rounded-2xl bg-sand/70 border border-forest/10 p-3 flex items-center gap-3 text-left" data-testid="booking-prefill">
              <span className="grid place-items-center h-9 w-9 rounded-full bg-forest/10 text-forest shrink-0">
                <CheckCircle2 className="h-4 w-4" />
              </span>
              <div className="text-sm min-w-0">
                <p className="font-semibold text-forest truncate">Booking for {done.name}</p>
                <p className="text-forest/55 text-xs truncate">{done.email}{done.phone ? ` · ${done.phone}` : ""}</p>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 text-left">
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.14em] font-bold text-sage mb-1.5 block">Preferred date</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="booking-date-trigger"
                      className="h-12 w-full rounded-xl bg-white border border-forest/15 px-3 flex items-center gap-2 text-left text-forest text-sm hover:border-forest/30 transition-colors"
                    >
                      <CalendarDays className="h-4 w-4 text-forest/50" />
                      {date ? format(date, "EEE, MMM d") : <span className="text-forest/40">Pick a date</span>}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="p-0 w-auto">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => {
                        const day = d.getDay();
                        return d < today || day === 0 || day === 6;
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.14em] font-bold text-sage mb-1.5 block">Preferred time</span>
                <Select value={time} onValueChange={setTime}>
                  <SelectTrigger className="h-12 rounded-xl bg-white border-forest/15 text-forest" data-testid="booking-time-trigger">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-forest/50" />
                      <SelectValue placeholder="Pick a time" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
            </div>

            <div className="mt-3 text-left">
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anything we should know? (optional)"
                data-testid="booking-notes"
                rows={2}
                className="rounded-xl bg-white border-forest/15 text-forest resize-none"
              />
            </div>

            <Button
              onClick={book}
              disabled={booking}
              data-testid="booking-submit"
              className="mt-5 w-full rounded-full bg-terracotta hover:bg-terracotta-dark text-cream h-12 font-semibold shadow-lg transition-all duration-300 hover:-translate-y-0.5 active:scale-95"
            >
              {booking ? <Loader2 className="h-5 w-5 animate-spin" /> : (<>Book my meeting <ArrowRight className="h-4 w-4 ml-2" /></>)}
            </Button>

            <button
              onClick={() => navigate("/")}
              data-testid="booking-skip"
              className="mt-5 text-sm text-forest/50 hover:text-forest underline underline-offset-4 transition-colors"
            >
              Skip for now — we&apos;ll call you
            </button>
            <SmsConsent className="mt-6 text-center" />
          </>
        )}
      </motion.div>
    </div>
  );
}
